import {
    mdiHistory,
    mdiPaperclip,
    mdiTextBoxPlusOutline,
    mdiSecurity,
    mdiInformationOutline,
    mdiFileClockOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { Stack, Tab, Tabs } from '@mui/material';
import React, { Dispatch, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ProjectSidepanel, setActiveSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import { IAppReduxStore } from '../../../redux/store';
import SidepanelDocuments from './SidepanelDocuments';
import SidepanelFiles from './SidepanelFiles';
import SidepanelGeneral from './SidepanelGeneral';
import SidepanelHistory from './SidepanelHistory';
import SidepanelPermissions from './SidepanelPermissions';
import { getTrimmedString } from '../../../util/UiUtils';
import { getDocTypeOrDefault } from '../../../util/DocUtils';
import SidepanelHeader from './SidepanelHeader';
import SidepanelWrapper from './SidepanelWrapper';
import DocnumberSidepanel from './DocnumberSidepanel';
import { fetchDocuments } from '../../../util/ProjectUtils';
import SidepanelActivities from './SidepanelActivities';

/**
 * Sidepanel container for showing information on a given document.
 */
export default () => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get the replicaId, areaName, viewName and category from the react router. */
    const params = useParams();
    const { replicaId, areaName, viewName, category } = params;

    /** Get relevant values from Redux store. */
    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);
    const { active: activeTab, targetDocId: universalId } = useSelector(
        (store: IAppReduxStore) => store.projectSidepanel,
    );
    const documents = useSelector((store: IAppReduxStore) => store.projectDocuments.data);
    const importDocuments = useSelector((store: IAppReduxStore) => store.importDocuments.data);
    const additionalDocuments = useSelector((store: IAppReduxStore) => store.projectAdditionalDocs.documents.data);
    const searchDocuments = useSelector((store: IAppReduxStore) => store.projectAdvancedSearch.data);
    const areaSearchText = useSelector((store: IAppReduxStore) => store.projectAdvancedSearch.areaSearchText);
    const docnumberSidepanelOpen = useSelector(
        (_store: IAppReduxStore) => _store.docnumberSidepanel.isSidepanelOpen,
    );

    /** State that shows if the sidepanel is expanded. */
    const [isExpanded, setExpanded] = useState<boolean>(false);

    /**
     * Close the sidepanel when the project, project view, or view category changes.
     */
    useEffect(() => {
        if (activeTab !== ProjectSidepanel.None) return;
        dispatch(setActiveSidepanel(ProjectSidepanel.None));
    }, [replicaId, areaName, viewName, category]);

    /** Returns the array of the additional documents.  */
    const additionalDocument = Object.values(additionalDocuments).reduce((arr, item) => {
        return [...arr, ...item];
    }, []);

    /** Find the document/additional document the user clicked on.  */
    const document = (
        areaSearchText
            ? [...searchDocuments, ...additionalDocument]
            : [...documents, ...importDocuments, ...additionalDocument]
    )?.find(x => x.UniversalID === universalId);

    const documentType = getDocTypeOrDefault(document, docTypes);

    /** Concatenating DocNumber and DocIndex to use as the firstRow of the Sidepanel.  */
    const firstRow =
        document &&
        getTrimmedString(document.DocNumber) + ' ' + getTrimmedString(document.DocIndex);

    /**
     * Closes the sidepanel.
     */
    const handleClose = () => dispatch(setActiveSidepanel(ProjectSidepanel.None));

    /* Handles expanding/collapsing the sidepanel */
    const handleExpand = () => {
        setExpanded(!isExpanded);
    };

    /**
     * Updates the active tab.
     * @param _ - React event (This parameter is not used).
     * @param value - New Active Tab value.
     */
    const handleChange = (_: React.SyntheticEvent, value: ProjectSidepanel) =>
        dispatch(setActiveSidepanel(value));

    /**
     * Gets the Sidebar's contents based on the active tab.
     */
    const getTabContent = useCallback(() => {
        switch (activeTab) {
            case ProjectSidepanel.General:
                return <SidepanelGeneral />;

            case ProjectSidepanel.Files:
                return <SidepanelFiles />;

            case ProjectSidepanel.Documents:
                return <SidepanelDocuments />;

            case ProjectSidepanel.Activities:
                return <SidepanelActivities />;

            case ProjectSidepanel.History:
                return <SidepanelHistory />;

            case ProjectSidepanel.Permissions:
                return <SidepanelPermissions />;
        }
    }, [activeTab]);

    if (docnumberSidepanelOpen) {
        return <DocnumberSidepanel onSaved={() => fetchDocuments(params)} />;
    }

    if (!document || activeTab === ProjectSidepanel.None) {
        return <></>;
    }

    return (
        <SidepanelWrapper isExpanded={isExpanded} addDivider={true}>
            <SidepanelHeader
                sx={{ flexGrow: 0, flexShrink: 0 }}
                type={documentType}
                firtsRowTitle={firstRow}
                secondRowTitle={document.Subject}
                handleClose={handleClose}
                handleExpand={handleExpand}
                isExpanded={isExpanded}
            />
            <Stack sx={{ flexGrow: 4, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleChange}
                    sx={{ '& .MuiButtonBase-root': { minWidth: 56, p: 0, flexGrow: 1 } }}
                >
                    <Tab
                        icon={<Icon path={mdiInformationOutline} size={1} />}
                        value={ProjectSidepanel.General}
                    />
                    <Tab
                        icon={<Icon path={mdiPaperclip} size={1} />}
                        value={ProjectSidepanel.Files}
                    />
                    <Tab
                        icon={<Icon path={mdiTextBoxPlusOutline} size={1} />}
                        value={ProjectSidepanel.Documents}
                    />
                    <Tab
                        icon={<Icon path={mdiFileClockOutline} size={1} />}
                        value={ProjectSidepanel.Activities}
                    />
                    <Tab
                        icon={<Icon path={mdiHistory} size={1} />}
                        value={ProjectSidepanel.History}
                    />
                    <Tab
                        icon={<Icon path={mdiSecurity} size={1} />}
                        value={ProjectSidepanel.Permissions}
                    />
                </Tabs>
                {getTabContent()}
            </Stack>
        </SidepanelWrapper>
    );
};
